import { ErrorMessage, Field, FieldProps, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import DateCustomInput from "../../../../components/custom/date-input-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";

import { showToast } from "../../../../components/custom/custom-toast";
import ImageCustom from "../../../../components/custom/image-custom";
import { SelectOneDropdown } from "../../../../components/custom/select-one-dropdown";
import { IconEllipse, IconUserDefault } from "../../../../components/icons/icon-group";
import { GENDER } from "../../../../constants/status";
import { useFetchData } from "../../../../hooks/useFetch";
import useUpload from "../../../../hooks/useUpload";
import { BrandManagerRequest } from "../../../../models/requests/brand-manager-request";
import { DetailBrandResponse } from "../../../../models/response/brand-response";
import { CompaniesResponse } from "../../../../models/response/companies";
import { API_BRANDS_ROUTER, API_UPLOAD_ROUTER } from "../../../../services/api-config";
import { checkUsername, handleIsValidInput } from "../../../../utils/helpers";
import { convertDateFormat, getTodayDate } from "../../../../utils/time-utils";

interface BrandCreateProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsOpenInfo: (value: boolean) => void;
  setDataDetail: (value: DetailBrandResponse) => void;
  setRefresh: (value: boolean) => void;
  dataCompanies: CompaniesResponse[];
}

export const BrandCreate = ({
  isOpen,
  setIsOpen,
  setIsOpenInfo,
  setDataDetail,
  setRefresh,
  dataCompanies,
}: BrandCreateProps) => {
  const formikRef = useRef<FormikProps<any> | null>(null);
  const [imageUpload, setImageUpload] = useState("");
  const [imageDisplay, setImageDisplay] = useState("");

  const { request: fetchDataCreate, baseResponse: responsesCreate } = useFetchData.Post();
  const { uploadFiles: onUploadFile, baseResponse: responseUpload } = useUpload<any>(API_UPLOAD_ROUTER.API_UPLOAD);

  useEffect(() => {
    if (!isOpen) {
      formikRef.current?.resetForm();
      setImageDisplay("");
    }
  }, [isOpen]);

  const handleCreateEmployee = (values: any) => {
    const newData = new BrandManagerRequest();
    newData.name = values.name;
    newData.username = values.username.toLocaleLowerCase();
    newData.birthday = values.birthday && convertDateFormat(values.birthday, "yyyy-MM-dd", "dd/MM/yyyy");
    newData.gender = values.gender === "Nam" ? 1 : 0;
    newData.phone = values.phone;
    newData.address = values.address;
    newData.id_card_number = values.id_card_number;
    newData.avatar = imageUpload;
    newData.joining_date = values.joining_date && convertDateFormat(values.joining_date, "yyyy-MM-dd", "dd/MM/yyyy");
    newData.brand_name = values.brand_name;
    newData.identifier_name = values.identifier_name;
    newData.company_id = values.company_id;
    fetchDataCreate({
      endPoint: API_BRANDS_ROUTER.API_CREATE_BRAND(),
      data: newData,
    });
  };

  useEffect(() => {
    if (!responsesCreate) return;
    if (responsesCreate.status === 200) {
      showToast({ message: "Thêm mới thành công", type: "success" });
      setIsOpenInfo(true);
      setIsOpen(false);
      setDataDetail(responsesCreate.data as DetailBrandResponse);
      setRefresh(true);
    } else {
      showToast({ message: responsesCreate.message, type: "error" });
    }
  }, [responsesCreate]);

  useEffect(() => {
    if (!responseUpload) return;
    if (responseUpload.status === 200 && responseUpload.data.length > 0) {
      setImageUpload(responseUpload.data[0]);
    }
  }, [responseUpload]);

  const SubmittedForm = Yup.object().shape({
    name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(100, "Độ dài tối đa 100 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    brand_name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(100, "Độ dài tối đa 100 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    identifier_name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(50, "Độ dài tối đa 50 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    username: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(50, "Độ dài tối đa 50 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    phone: Yup.string().min(10, "Số điện thoại chưa đúng 10 hoặc 11 ký tự"),
    address: Yup.string().max(100, "Độ dài tối đa 100 ký tự"),
    id_card_number: Yup.string().min(9, "Độ dài tối thiểu 9 ký tự").max(12, "Độ dài tối đa 12 ký tự"),
    company_id: Yup.string().required("Vui lòng chọn công ty"),
  });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files; // Lấy tệp hình ảnh
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageDisplay(reader.result as string); // Cập nhật URL hình ảnh
      };
      reader.readAsDataURL(file[0]); // Đọc tệp dưới dạng URL
      onUploadFile(file);
    }
  };
  const handleDateChange = (date: string, form: any, field: { name: string }) => {
    const formattedDate = convertDateFormat(date, "dd/MM/yyyy", "yyyy-MM-dd");
    form.setFieldValue(field.name, formattedDate);
  };

  const formatFieldDate = (value: string) => {
    return value ? convertDateFormat(value, "yyyy-MM-dd", "dd/MM/yyyy") : "";
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        name: "",
        birthday: "",
        joining_date: "",
        gender: 1,
        avatar: "",
        phone: "",
        address: "",
        id_card_number: "",
        identifier_name: "",
        brand_name: "",
        username: "",
        company_id: "",
      }}
      validationSchema={SubmittedForm}
      onSubmit={(values) => {
        handleCreateEmployee(values);
      }}
    >
      {({ values, submitForm, resetForm, setFieldValue }) => {
        const bodyModal = () => {
          return (
            <Form className="">
              <div className="flex flex-wrap items-start gap-4">
                {/* BRAND */}
                <div className="w-full bg-white p-4 pt-0 rounded-md">
                  <div className="font-bold text-gray-600 py-2">* Thương hiệu:</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="brand_name">
                        Tên thương hiệu <span className="text-red-500">*</span>
                      </label>
                      <Field
                        value={values.brand_name}
                        name="brand_name"
                        type="text"
                        id="brand_name"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        maxLength={100}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputValue = e.target.value;
                          if (inputValue.length <= 100 && handleIsValidInput(inputValue)) {
                            console.log("inputValue", inputValue);
                            setFieldValue("brand_name", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="brand_name" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label htmlFor="identifier_name">
                        Tên định danh <span className="text-red-500">*</span>
                      </label>
                      <Field
                        value={values.identifier_name}
                        name="identifier_name"
                        type="text"
                        id="identifier_name"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        maxLength={50}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputValue = e.target.value;
                          if (inputValue.length <= 50 && checkUsername(inputValue)) {
                            setFieldValue("identifier_name", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="identifier_name" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                    <div className="mt-3">
                      <label htmlFor="company_id">
                        Tên công ty <span className="text-red-500">*</span>
                      </label>
                      <Field name="company_id">
                        {() => (
                          <SelectOneDropdown
                            selectdId={values.company_id}
                            placeholder="Chọn công ty"
                            dataOptions={dataCompanies}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option._id}
                            onChange={(selectedOption) => {
                              setFieldValue("company_id", selectedOption ? selectedOption._id : "");
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="company_id" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>
                </div>

                {/* ACCOUNT */}
                <div className="w-full bg-white p-4 pt-0 rounded-md">
                  <div className="font-bold text-gray-600 py-2">* Tài khoản:</div>

                  <button type="button" className="flex flex-col items-center justify-center w-full relative">
                    <span className="w-24 h-24 relative">
                      <ImageCustom
                        url={`${imageDisplay || IconUserDefault}`}
                        className="w-24 h-24 rounded-full overflow-hidden object-cover"
                      />
                      <input
                        type="file"
                        id="fileUpload"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <IconEllipse className="w-24 h-24 absolute inset-0 flex items-center justify-center" />
                    </span>
                    <label htmlFor="fileUpload" className="mt-1 text-blue-500 text-xs font-medium cursor-pointer">
                      Tải hình đại diện
                    </label>
                  </button>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="name">
                        Tên nhân viên quản trị <span className="text-red-500">*</span>
                      </label>
                      <Field
                        value={values.name}
                        name="name"
                        type="text"
                        id="name"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        maxLength={100}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputValue = e.target.value;
                          if (inputValue.length <= 100 && handleIsValidInput(inputValue)) {
                            console.log("inputValue", inputValue);
                            setFieldValue("name", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="name" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                    <div>
                      <label htmlFor="gender">Giới tính</label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center mt-2">
                          <Field
                            type="radio"
                            name="gender"
                            value={GENDER.MALE}
                            className="form-radio"
                            onChange={() => setFieldValue("gender", GENDER.MALE)}
                          />
                          <span className="">Nam</span>
                        </label>
                        <label className="flex items-center mt-2">
                          <Field
                            type="radio"
                            name="gender"
                            value={GENDER.FEMALE}
                            className="form-radio"
                            onChange={() => setFieldValue("gender", GENDER.FEMALE)}
                          />
                          <span className="">Nữ</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="username">
                        Tên đăng nhập <span className="text-red-500">*</span>
                      </label>
                      <Field
                        value={values.username}
                        name="username"
                        type="text"
                        id="username"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        maxLength={50}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputValue = e.target.value;
                          if (inputValue.length <= 50 && checkUsername(inputValue)) {
                            setFieldValue("username", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="username" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                    <div>
                      <label htmlFor="birthday">Ngày sinh</label>
                      <Field name="birthday">
                        {({ field, form }: FieldProps) => (
                          <DateCustomInput
                            maxDate={getTodayDate()}
                            label="Ngày sinh"
                            value={formatFieldDate(field.value)}
                            onChange={(date: string) => handleDateChange(date, form, field)}
                          />
                        )}
                      </Field>
                    </div>
                    <div>
                      <label htmlFor="joining_date">Ngày làm việc</label>
                      <Field name="joining_date">
                        {({ field, form }: FieldProps) => (
                          <DateCustomInput
                            value={formatFieldDate(field.value)}
                            onChange={(date) => handleDateChange(date, form, field)}
                          />
                        )}
                      </Field>
                    </div>
                    <div>
                      <label htmlFor="phone">Số điện thoại</label>
                      <Field
                        data-testid="phone-input"
                        value={values.phone}
                        name="phone"
                        type="text"
                        id="phone"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        minLength={10}
                        maxLength={11}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          let inputValue = e.target.value.replace(/\D/g, "");
                          if (inputValue.length > 0 && !inputValue[0].startsWith("0")) {
                            inputValue = "0" + inputValue;
                          }
                          if (inputValue.length <= 11) {
                            setFieldValue("phone", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="phone" component="div" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label htmlFor="address">Địa chỉ</label>
                      <Field
                        value={values.address}
                        name="address"
                        type="text"
                        id="address"
                        maxLength={100}
                        placeholder="Nhập thông tin"
                        className="form-input"
                      />
                      <ErrorMessage name="address" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                    <div>
                      <label htmlFor="id_card_number">CCCD</label>
                      <Field
                        value={values.id_card_number}
                        name="id_card_number"
                        type="text"
                        id="id_card_number"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        minLength={9}
                        maxLength={12}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          let inputValue = e.target.value.replace(/\D/g, "");
                          if (inputValue.length <= 12) {
                            setFieldValue("id_card_number", inputValue);
                          }
                        }}
                      />
                      <ErrorMessage name="id_card_number" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        };

        const footerModal = () => (
          <>
            <ButtonCustom
              type="button"
              text="Hủy"
              onClick={() => {
                resetForm();
                setIsOpen(false);
              }}
              className="bg-gray-100 text-gray-500 hover:bg-gray-400 hover:text-white"
            />
            <ButtonCustom type="button" text="Lưu" onClick={submitForm} />
          </>
        );

        return (
          <ModalCustom
            data-testid="modal-custom"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            titleModal="Thêm mới thương hiệu"
            bodyModal={bodyModal()}
            footerModal={footerModal()}
            modalSize="80%"
          />
        );
      }}
    </Formik>
  );
};
